import { useState, useMemo, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY } from '../utils/constants';
import { useCruiseSettings, getCruiseDecks } from '../hooks/useCruiseSettings';
import { fmt12 } from './TimeField';
import { TimeEntry, SailingChips } from './TimeEntry';
import LineItemsEditor, { isBlankLine } from './LineItemsEditor';
import { agreedTotal } from '../utils/eventLines';
import { Button, Field, Panel } from './ui';
import { ArrowLeft, TriangleAlert } from 'lucide-react';
import { EVENT_TYPES, STATUSES, toHHMM, toDisplayDate } from './Events';
import EventPayments from './EventPayments';
import EventFlags from './EventFlags';
import { checkEventConflicts } from '../utils/eventConflicts';
import { istTodayIso } from '../utils/bookingWindow';

// ─── Event form — create, edit, cancel (Stage 2); payments (Stage 4); line items ──
// Writes the `events` row directly (RLS: is_staff insert/update, no delete).
// Cancelling is a status update that sets cancelled_at + cancellation_reason
// together — the table's CHECK constraint requires the pair, so the reason is
// collected here before anything is written. Nothing is ever deleted.
//
// Cruise chips carry STORED cruise names (cruise_settings.cruise_name), never
// display names: events are compared against bookings and sailings by that key.

const todayInput = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};
const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
const textOrNull = (v) => { const t = (v || '').trim(); return t ? t : null; };

const initialForm = (ev) => ({
  title:          ev?.title || '',
  event_type:     ev?.event_type || '',
  status:         ev?.status || 'tentative',
  event_date:     ev?.event_date || todayInput(),
  end_date:       ev?.end_date || '',
  start_time:     toHHMM(ev?.start_time),
  end_time:       toHHMM(ev?.end_time),
  cruise_types:   ev?.cruise_types || [],
  decks:          ev?.decks || [],
  pax_min:        ev?.pax_min ?? '',
  pax_max:        ev?.pax_max ?? '',
  menu:           ev?.menu || '',
  contact_name:   ev?.contact_name || '',
  contact_phone:  ev?.contact_phone || '',
  contact_email:  ev?.contact_email || '',
  lines:          [],   // event_line_items, loaded separately for a saved event
  partner_name:   ev?.partner_name || '',
  notes:          ev?.notes || '',
});

// Toggle chips for cruises and decks. Same vocabulary as the filter chips on
// the list; 44px tall on a phone so they can be hit standing on the jetty.
function ToggleChips({ options, selected, onToggle, emptyText }) {
  if (options.length === 0) {
    return <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-ink-muted)' }}>{emptyText}</span>;
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {options.map(opt => {
        const on = selected.includes(opt);
        return (
          <button key={opt} type="button" className="ev-chip-toggle" aria-pressed={on} onClick={() => onToggle(opt)}
            style={{
              background: on ? 'var(--color-accent-ring)' : 'var(--color-surface)',
              border: `1.5px solid ${on ? 'var(--color-accent)' : 'var(--color-border-strong)'}`,
              color: on ? 'var(--color-accent-strong)' : 'var(--color-ink-brand-softer)',
            }}>
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function GroupTitle({ children }) {
  return <div className="ev-group-title">{children}</div>;
}

// Time control with a clear action: TimeField has no empty option, and a
// single-evening event may legitimately have no fixed hours.
// Nothing sails before mid-morning. A start before 10:00 is almost always a
// 12-hour time entered with AM left selected (KITE MANJA sat at 04:30 for
// months as a charter because nothing questioned it). Not a block: a visible
// note under the field that spells out the PM reading.
const EARLY_START = '10:00';
function pmReading(hhmm) {
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m) || h >= 12) return '';
  return fmt12(`${String(h + 12).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
}


export default function EventForm({ editData, onNavigate }) {
  const isEdit = !!editData?.id;
  // Home and the Bookings screen open this form for a date; they pass returnTo
  // (a screen key) and returnDate (DD-MM-YYYY) so Save and Back land there.
  const goBack = () => onNavigate(editData?.returnTo || 'events', editData?.returnDate);
  const wasCancelled = editData?.status === 'cancelled';
  const { settings } = useCruiseSettings();

  const [form, setForm] = useState(() => initialForm(editData));
  // Line items of the SAVED event: the ids drive the save-time diff, and their
  // total is what the payments panel measures outstanding against.
  const [savedLines, setSavedLines] = useState([]);
  const [lineErrors, setLineErrors] = useState({});
  useEffect(() => {
    if (!isEdit) return undefined;
    let cancelled = false;
    supabase.from('event_line_items')
      .select('id, title, unit_amount, quantity, tax_percent, sort_order')
      .eq('event_id', editData.id)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true })
      .then(({ data, error: err }) => {
        if (cancelled || err) return;
        const rows = (data || []).map(l => ({ id: l.id, title: l.title, unit_amount: String(l.unit_amount), quantity: String(l.quantity), tax_percent: String(l.tax_percent) }));
        setSavedLines(data || []);
        setForm(f => ({ ...f, lines: rows }));
      });
    return () => { cancelled = true; };
  }, [isEdit, editData]);

  // Conflict flags for the SAVED event (Stage 6): display only, after paint.
  const [flags, setFlags] = useState(null);
  useEffect(() => {
    if (!isEdit) return undefined;
    let cancelled = false;
    const timer = setTimeout(() => {
      checkEventConflicts([editData], { today: istTodayIso() })
        .then(m => { if (!cancelled) setFlags(m.get(editData.id) || []); })
        .catch(() => { if (!cancelled) setFlags([]); });
    }, 0);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [isEdit, editData]);
  const earlyStart = !!form.start_time && form.start_time < EARLY_START;
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));
  const onInput = (k) => (e) => set(k)(e.target.value);

  const cruiseOptions = useMemo(() => {
    const names = settings.map(s => s.cruise_name);
    // Keep an existing value selectable even if that cruise is now inactive.
    form.cruise_types.forEach(c => { if (!names.includes(c)) names.push(c); });
    return names;
  }, [settings, form.cruise_types]);

  const deckOptions = useMemo(() => {
    const chosen = form.cruise_types.length ? settings.filter(s => form.cruise_types.includes(s.cruise_name)) : settings;
    const decks = [];
    chosen.forEach(s => getCruiseDecks(s).forEach(d => { if (!decks.includes(d)) decks.push(d); }));
    // Migrated charters may carry 'Both' or another legacy value; never lose it.
    form.decks.forEach(d => { if (!decks.includes(d)) decks.push(d); });
    return decks;
  }, [settings, form.cruise_types, form.decks]);

  const toggleIn = (k) => (opt) => setForm(f => ({ ...f, [k]: f[k].includes(opt) ? f[k].filter(x => x !== opt) : [...f[k], opt] }));

  // The select never offers "Cancelled" — cancelling needs a reason and goes
  // through the panel below. A cancelled event shows it so the form reads
  // true, and picking any other status reinstates the event.
  const statusOptions = STATUSES.filter(s => s.value !== 'cancelled' || wasCancelled);

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Give the event a title.';
    if (!form.event_type) e.event_type = 'Choose a type.';
    if (!form.event_date) e.event_date = 'Choose a date.';
    if (form.end_date && form.end_date < form.event_date) e.end_date = 'End date is before the start.';
    if (form.start_time && form.end_time && !form.end_date && form.end_time <= form.start_time) e.end_time = 'End time is before the start.';
    const pmin = numOrNull(form.pax_min), pmax = numOrNull(form.pax_max);
    if (pmin !== null && (pmin < 0 || !Number.isInteger(pmin))) e.pax_min = 'Whole number.';
    if (pmax !== null && (pmax < 0 || !Number.isInteger(pmax))) e.pax_max = 'Whole number.';
    if (pmin !== null && pmax !== null && pmax < pmin) e.pax_max = 'Must be at least the minimum.';
    const le = {};
    form.lines.forEach((l, i) => {
      if (isBlankLine(l)) return;
      const err = {};
      if (!l.title.trim()) err.title = 'Name the item.';
      const amt = Number(l.unit_amount);
      if (l.unit_amount === '' || Number.isNaN(amt)) err.unit_amount = 'Amount.';
      const qty = l.quantity === '' ? 1 : Number(l.quantity);
      if (Number.isNaN(qty) || qty <= 0) err.quantity = 'Above zero.';
      const tax = l.tax_percent === '' ? 0 : Number(l.tax_percent);
      if (Number.isNaN(tax) || tax < 0 || tax > 100) err.tax_percent = '0 to 100.';
      if (Object.keys(err).length) le[i] = err;
    });
    setLineErrors(le);
    if (Object.keys(le).length) e.lines = 'Check the line items.';
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    setError('');
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;

    const payload = {
      title:         form.title.trim(),
      event_type:    form.event_type,
      status:        form.status,
      event_date:    form.event_date,
      end_date:      form.end_date || null,
      start_time:    form.start_time || null,
      end_time:      form.end_time || null,
      cruise_types:  form.cruise_types.length ? form.cruise_types : null,
      decks:         form.decks.length ? form.decks : null,
      pax_min:       numOrNull(form.pax_min),
      pax_max:       numOrNull(form.pax_max),
      menu:          textOrNull(form.menu),
      contact_name:  textOrNull(form.contact_name),
      contact_phone: textOrNull(form.contact_phone),
      contact_email: textOrNull(form.contact_email),
      partner_name:  textOrNull(form.partner_name),
      notes:         textOrNull(form.notes),
    };
    // Reinstating: the CHECK constraint forbids cancelled_at on a live status.
    if (wasCancelled && form.status !== 'cancelled') {
      payload.cancelled_at = null;
      payload.cancellation_reason = null;
    }

    setSaving(true);
    const result = isEdit
      ? await supabase.from('events').update(payload).eq('id', editData.id).select('id').single()
      : await supabase.from('events').insert(payload).select('id').single();
    if (result.error) { setSaving(false); setError(`Could not save: ${result.error.message}`); return; }
    const lineErr = await syncLines(result.data.id);
    setSaving(false);
    if (lineErr) { setError(`Event saved, but the line items were not: ${lineErr}`); return; }
    goBack();
  };

  // Line items: the saved rows and the form's rows are diffed by id. Blank
  // rows are dropped; removed rows are deleted; kept rows are updated in
  // place; new rows are inserted. Returns an error message or null.
  const syncLines = async (eventId) => {
    const clean = form.lines.filter(l => !isBlankLine(l)).map((l, i) => ({
      ...(l.id ? { id: l.id } : {}),
      event_id: eventId,
      title: l.title.trim(),
      unit_amount: Number(l.unit_amount),
      quantity: l.quantity === '' ? 1 : Number(l.quantity),
      tax_percent: l.tax_percent === '' ? 0 : Number(l.tax_percent),
      sort_order: i,
    }));
    const keep = new Set(clean.filter(l => l.id).map(l => l.id));
    const gone = savedLines.map(l => l.id).filter(id => !keep.has(id));
    if (gone.length) {
      const { error: err } = await supabase.from('event_line_items').delete().in('id', gone);
      if (err) return err.message;
    }
    const updates = clean.filter(l => l.id);
    if (updates.length) {
      const { error: err } = await supabase.from('event_line_items').upsert(updates);
      if (err) return err.message;
    }
    const inserts = clean.filter(l => !l.id);
    if (inserts.length) {
      const { error: err } = await supabase.from('event_line_items').insert(inserts);
      if (err) return err.message;
    }
    return null;
  };

  const handleCancelEvent = async () => {
    const reason = cancelReason.trim();
    if (!reason) { setError('Say why the event is cancelled.'); return; }
    setError('');
    setCancelling(true);
    const { error: err } = await supabase
      .from('events')
      .update({ status: 'cancelled', cancelled_at: new Date().toISOString(), cancellation_reason: reason })
      .eq('id', editData.id);
    setCancelling(false);
    if (err) { setError(`Could not cancel: ${err.message}`); return; }
    goBack();
  };

  const title = isEdit ? 'Edit event' : 'New event';

  return (
    <div>
      <div className="back-row">
        <button type="button" className="back-btn" onClick={goBack}>
          <ArrowLeft size={15} strokeWidth={2} aria-hidden="true" />{editData?.returnTo ? 'Back' : 'Events'}
        </button>
        <h2 style={{ fontSize: '1rem', fontWeight: 700, color: NAVY, margin: 0 }}>{title}</h2>
      </div>

      {wasCancelled && (
        <div className="toast toast-error" role="status">
          Cancelled {editData.cancelled_at ? `on ${toDisplayDate(editData.cancelled_at.slice(0, 10))}` : ''}: {editData.cancellation_reason || 'no reason recorded'}.
          Choose another status below to reinstate it.
        </div>
      )}

      {flags && flags.length > 0 && (
        <Panel tone="sunken" className="ev-flags-panel" role="status" aria-label="Worth a look">
          <div style={{ fontWeight: 700, color: NAVY, fontSize: 'var(--text-sm)', marginBottom: 4 }}>Worth a look</div>
          <EventFlags lines={flags} />
        </Panel>
      )}

      <form className="form-card ev-form" onSubmit={handleSubmit} noValidate>
        {error && <div className="toast toast-error" role="alert">{error}</div>}

        <section className="ev-group">
          <GroupTitle>When and what</GroupTitle>
          <div className="form-grid">
            <Field label="Title" htmlFor="ev-title" required error={errors.title} fullWidth>
              <input id="ev-title" type="text" value={form.title} onChange={onInput('title')} placeholder="Sharma wedding, Infosys offsite, dry dock" autoComplete="off" />
            </Field>
            <Field label="Type" htmlFor="ev-type" required error={errors.event_type}>
              <select id="ev-type" value={form.event_type} onChange={onInput('event_type')}>
                <option value="">Select type</option>
                {EVENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Status" htmlFor="ev-status" required hint={wasCancelled && form.status !== 'cancelled' ? 'Saving reinstates this event.' : undefined}>
              <select id="ev-status" value={form.status} onChange={onInput('status')}>
                {statusOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </Field>
            <Field label="Date" htmlFor="ev-date" required error={errors.event_date}>
              <input id="ev-date" type="date" value={form.event_date} onChange={onInput('event_date')} style={{ colorScheme: 'light' }} />
            </Field>
            <Field label="End date" htmlFor="ev-end-date" error={errors.end_date} hint="Only for events over more than one day.">
              <input id="ev-end-date" type="date" value={form.end_date} min={form.event_date || undefined} onChange={onInput('end_date')} style={{ colorScheme: 'light' }} />
            </Field>
            <Field label="Start time" htmlFor="ev-start">
              <TimeEntry id="ev-start" value={form.start_time} onChange={set('start_time')} />
              {earlyStart && (
                <span className="ev-time-warn" role="status">
                  <TriangleAlert size={14} strokeWidth={1.75} aria-hidden="true" />
                  No cruise sails this early. Did you mean {pmReading(form.start_time)}?
                </span>
              )}
            </Field>
            <Field label="End time" htmlFor="ev-end" error={errors.end_time}>
              <TimeEntry id="ev-end" value={form.end_time} onChange={set('end_time')} />
            </Field>
            <Field label="Sailings on this date" fullWidth hint="One tap sets both times to that sailing. Type a time above for anything else.">
              <SailingChips date={form.event_date} start={form.start_time} end={form.end_time}
                            onPick={(dep, fin) => setForm(f => ({ ...f, start_time: dep, end_time: fin }))} />
            </Field>
          </div>
        </section>

        <section className="ev-group">
          <GroupTitle>Who</GroupTitle>
          <div className="form-grid">
            <Field label="Contact name" htmlFor="ev-contact">
              <input id="ev-contact" type="text" value={form.contact_name} onChange={onInput('contact_name')} autoComplete="off" />
            </Field>
            <Field label="Contact phone" htmlFor="ev-phone">
              <input id="ev-phone" type="tel" inputMode="tel" value={form.contact_phone} onChange={onInput('contact_phone')} placeholder="+91" autoComplete="off" />
            </Field>
            <Field label="Contact email" htmlFor="ev-email">
              <input id="ev-email" type="email" inputMode="email" value={form.contact_email} onChange={onInput('contact_email')} autoComplete="off" />
            </Field>
            <Field label="Partner name" htmlFor="ev-partner" hint="If the event came through a partner.">
              <input id="ev-partner" type="text" value={form.partner_name} onChange={onInput('partner_name')} autoComplete="off" />
            </Field>
            <div className="form-group">
              <label htmlFor="ev-pax-min">Pax</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input id="ev-pax-min" type="number" inputMode="numeric" min="0" step="1" value={form.pax_min} onChange={onInput('pax_min')} placeholder="From" style={{ width: '100%', minWidth: 0 }} aria-invalid={!!errors.pax_min} />
                <span style={{ color: 'var(--color-ink-muted)', fontSize: 'var(--text-sm)' }}>to</span>
                <input id="ev-pax-max" type="number" inputMode="numeric" min="0" step="1" value={form.pax_max} onChange={onInput('pax_max')} placeholder="To" aria-label="Pax maximum" style={{ width: '100%', minWidth: 0 }} aria-invalid={!!errors.pax_max} />
              </div>
              {(errors.pax_min || errors.pax_max)
                ? <span className="field-error">{errors.pax_min || errors.pax_max}</span>
                : <span className="calc-note">A range while tentative. One number is fine.</span>}
            </div>
          </div>
        </section>

        <section className="ev-group">
          <GroupTitle>On board</GroupTitle>
          <div className="form-grid">
            <Field label="Cruises" fullWidth hint="Which sailings the event sits on. Leave empty if it has its own hours.">
              <ToggleChips options={cruiseOptions} selected={form.cruise_types} onToggle={toggleIn('cruise_types')} emptyText="Loading cruises" />
            </Field>
            <Field label="Decks" fullWidth hint="None selected means the whole vessel.">
              <ToggleChips options={deckOptions} selected={form.decks} onToggle={toggleIn('decks')} emptyText="No decks for the chosen cruises" />
            </Field>
            <Field label="Menu" htmlFor="ev-menu" fullWidth>
              <textarea id="ev-menu" value={form.menu} onChange={onInput('menu')} placeholder="Courses, dietary notes, bar arrangement" rows={3} />
            </Field>
          </div>
        </section>

        <section className="ev-group">
          <GroupTitle>Money</GroupTitle>
          <p style={{ margin: '0 0 10px', fontSize: 'var(--text-xs)', color: 'var(--color-ink-muted)' }}>
            The agreed amount, as the lines that make it up. Working-out for the team, not a document.
            {errors.lines && <span className="field-error" style={{ display: 'block', marginTop: 4 }}>{errors.lines}</span>}
          </p>
          <LineItemsEditor lines={form.lines} onChange={(lines) => setForm(f => ({ ...f, lines }))} errors={lineErrors} />
        </section>

        <section className="ev-group">
          <GroupTitle>Notes</GroupTitle>
          <div className="form-grid">
            <Field htmlFor="ev-notes" fullWidth>
              <textarea id="ev-notes" aria-label="Notes" value={form.notes} onChange={onInput('notes')} rows={3} placeholder="Anything the team should know" />
            </Field>
          </div>
        </section>

        <div className="ev-form-actions" style={{ marginTop: 24 }}>
          <Button type="submit" variant="primary" disabled={saving || cancelling}>
            {saving ? 'Saving' : isEdit ? 'Save changes' : 'Save event'}
          </Button>
          <Button variant="secondary" onClick={goBack} disabled={saving || cancelling}>Back</Button>
          {isEdit && !wasCancelled && !cancelOpen && (
            <Button variant="danger" onClick={() => { setError(''); setCancelOpen(true); }} disabled={saving} style={{ marginLeft: 'auto' }}>
              Cancel event
            </Button>
          )}
        </div>

        {cancelOpen && (
          <Panel tone="sunken" style={{ marginTop: 18 }} role="group" aria-labelledby="ev-cancel-title">
            <div id="ev-cancel-title" style={{ fontWeight: 700, color: NAVY, fontSize: 'var(--text-base)', marginBottom: 4 }}>Cancel this event</div>
            <p style={{ margin: '0 0 12px', fontSize: 'var(--text-sm)', color: 'var(--color-ink-secondary)' }}>
              The event stays in history as cancelled. Any money already received is unchanged.
            </p>
            <Field label="Reason" htmlFor="ev-cancel-reason" required>
              <textarea id="ev-cancel-reason" value={cancelReason} onChange={e => setCancelReason(e.target.value)} rows={2} placeholder="Why it is not going ahead" />
            </Field>
            <div className="ev-form-actions" style={{ marginTop: 12 }}>
              <Button variant="danger" onClick={handleCancelEvent} disabled={cancelling || !cancelReason.trim()}>
                {cancelling ? 'Cancelling' : 'Confirm cancellation'}
              </Button>
              <Button variant="secondary" onClick={() => { setCancelOpen(false); setCancelReason(''); }} disabled={cancelling}>Keep event</Button>
            </div>
          </Panel>
        )}
      </form>

      {/* Money against the event. Lives outside the <form> so its own inputs
          and buttons never submit the event. Only once the event exists. */}
      {isEdit && <EventPayments eventId={editData.id} agreedAmount={agreedTotal(savedLines)} />}
    </div>
  );
}
